// Search in the header panel, after Louis Vuitton (snippets/header-search.liquid).
// As the visitor types, the product row below turns into the results ("3 Search Results") and a
// list of suggested searches drops down under the box (the typed part dark, the rest light), with
// a "Find our stores" link. Clearing the box brings back the default row. Uses Shopify's
// predictive search API; Enter still submits to the full search page.
if (!customElements.get('search-panel')) {
  customElements.define(
    'search-panel',
    class SearchPanel extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="search"]');
        this.clearButton = this.querySelector('[data-clear]');
        this.list = this.querySelector('[data-suggestions]');
        const panel = this.closest('.search-modal__content');
        this.heading = panel?.querySelector('[data-results-heading]');
        this.grid = panel?.querySelector('[data-results-grid]');
        if (!this.input || !this.list || !this.grid) return;

        this.defaultHeading = this.heading?.textContent || '';
        this.defaultGrid = this.grid.innerHTML;
        this.cache = new Map();

        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('focus', () => this.input.value.trim() && this.showList(true));
        this.input.addEventListener('keydown', (event) => this.onKey(event));
        this.clearButton?.addEventListener('click', () => this.clear());
        document.addEventListener('click', (event) => !this.contains(event.target) && this.showList(false));
      }

      onInput() {
        this.keepListClosed = false;
        const query = this.input.value.trim();
        this.clearButton.hidden = !this.input.value;
        clearTimeout(this.timer);
        if (!query) {
          this.reset();
          return;
        }
        this.timer = setTimeout(() => this.search(query), 180);
      }

      async search(query) {
        const data = await this.fetchResults(query);
        if (query !== this.input.value.trim()) return; // typed on meanwhile
        this.renderSuggestions(query, data.queries, data.products);
        this.renderResults(query, data.products);
      }

      async fetchResults(query) {
        if (!this.cache.has(query)) {
          const params = new URLSearchParams({
            q: query,
            'resources[type]': 'product,query',
            'resources[limit]': '10',
            'resources[options][unavailable_products]': 'last',
          });
          const root = window.Shopify?.routes?.root || '/';
          this.cache.set(
            query,
            fetch(`${root}search/suggest.json?${params}`)
              .then((response) => (response.ok ? response.json() : null))
              .then((json) => ({
                products: json?.resources?.results?.products || [],
                queries: json?.resources?.results?.queries || [],
              }))
              .catch(() => ({ products: [], queries: [] }))
          );
        }
        return this.cache.get(query);
      }

      renderSuggestions(query, queries, products = []) {
        // Shopify's query suggestions need search history, so also suggest matching product names
        const q = query.toLowerCase();
        const names = products.map((p) => p.title.toLowerCase()).filter((t) => t.includes(q));
        const texts = [...new Set([...queries.map((x) => x.text.toLowerCase()), ...names, q])].slice(0, 4);
        const items = texts.map((text) => {
          const li = document.createElement('li');
          li.setAttribute('role', 'option');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'search-panel__suggestion';
          button.innerHTML = this.dataset.searchIcon || '';
          const label = document.createElement('span');
          // Typed part in full colour, the completion lighter, like LV's suggestions
          const typed = text.toLowerCase().startsWith(query.toLowerCase()) ? query.length : 0;
          const strong = document.createElement('strong');
          strong.textContent = text.slice(0, typed);
          label.append(strong, text.slice(typed));
          button.appendChild(label);
          button.addEventListener('click', () => {
            this.keepListClosed = true;
            this.input.value = text;
            this.clearButton.hidden = false;
            this.showList(false);
            this.search(text);
          });
          li.appendChild(button);
          return li;
        });

        if (this.dataset.storesUrl) {
          const li = document.createElement('li');
          li.className = 'search-panel__stores';
          const link = document.createElement('a');
          link.href = this.dataset.storesUrl;
          link.textContent = this.dataset.storesLabel || 'Find our stores';
          li.appendChild(link);
          items.push(li);
        }
        this.list.replaceChildren(...items);
        this.showList(!this.keepListClosed && document.activeElement === this.input);
      }

      renderResults(query, products) {
        if (this.heading) {
          this.heading.textContent = products.length
            ? `${products.length} Search Result${products.length === 1 ? '' : 's'}`
            : `No results for “${query}”`;
        }
        this.grid.replaceChildren(...products.map((product) => this.tile(product)));
      }

      tile(product) {
        const item = document.createElement('li');
        item.className = 'product-tile grid__item';
        const image = product.featured_image?.url || product.image || '';
        const src = image ? `${image}${image.includes('?') ? '&' : '?'}width=900` : '';
        item.innerHTML = `
          <a class="product-tile__link" href="${product.url}">
            <span class="product-tile__media">${src ? `<img src="${src}" alt="" loading="lazy">` : ''}</span>
            <span class="product-tile__info">
              <span class="product-tile__title"></span>
              <span class="product-tile__price"></span>
            </span>
          </a>`;
        item.querySelector('.product-tile__title').textContent = product.title;
        item.querySelector('.product-tile__price').textContent = this.money(product.price);

        const heart = document.createElement('wishlist-button');
        heart.className = 'wishlist-heart product-tile__wishlist';
        heart.dataset.handle = product.handle;
        heart.dataset.title = product.title;
        heart.innerHTML = this.querySelector('template[data-heart]')?.innerHTML || '';
        item.appendChild(heart);
        return item;
      }

      reset() {
        this.showList(false);
        this.list.replaceChildren();
        if (this.heading) this.heading.textContent = this.defaultHeading;
        this.grid.innerHTML = this.defaultGrid;
      }

      clear() {
        this.input.value = '';
        this.clearButton.hidden = true;
        this.reset();
        this.input.focus();
      }

      showList(show) {
        this.list.hidden = !show || !this.list.children.length;
        this.input.setAttribute('aria-expanded', !this.list.hidden);
      }

      onKey(event) {
        if (event.key === 'Escape' && !this.list.hidden) {
          event.stopPropagation();
          this.showList(false);
        } else if (event.key === 'ArrowDown' && !this.list.hidden) {
          event.preventDefault();
          this.list.querySelector('button, a')?.focus();
        }
      }

      // Prices from the suggest API are strings like "75.00"; drop .00 like the rest of the store
      money(price) {
        const amount = Number(price);
        if (!Number.isFinite(amount)) return '';
        const whole = Number.isInteger(amount);
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        }).format(amount);
      }
    }
  );
}
